import { FC, useCallback, useState } from 'react';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import {
  AutocompleteRenderInputParams,
  Box,
  DatePicker,
  DragAndDrop,
  FormInputText,
  IconButton,
  InputLabel,
  Svg,
  Text,
  Timeline,
  TimePicker,
  Toggle,
} from '@hopskipdrive/react-components';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import { includes, isEmpty } from 'lodash';

import { colorTokens } from '../../../shared/components/variables';
import { useRiderGroupedOptions } from '../../hooks/useRiderGroupedOptions';
import { AccountLocationAttributes, EditRideDetails, RideOrganizer, RiderOnRide } from '../../types/rides';
import { getLeadOrganizerName } from '../../utils/helpers/organizer';
import { OutlinedCircle } from '../OutlineCircleNumber';

import { AdjustedArrivalFormField } from './AdjustedArrivalFormField';
import { LocationSearchDropdown } from './LocationSearchDropdown';
import { getDirtyFields, mergeDateTime } from './RideForm.helpers';
import { FormWrapper } from './RideForm.styles';
import { RiderDropdown } from './RiderDropdown';
import { useLocationOptions } from './useLocationOptions';

interface RideFormProps {
  timeZone: string;
  ridersOnRide: RiderOnRide[];
  organizers: RideOrganizer[];
  rideDetailsDefault: EditRideDetails & { time: string };
  handleSubmit: (formData: RideFormData) => void;
  isNewRide?: boolean;
}

export interface RideFormData {
  timeAnchor?: string;
  anchorTime?: Date;
  time?: string;
  adjustmentReasonKey?: string;
  waypointsAttributes: {
    anchor: boolean;
    id?: number;
    passengerIdsStrings?: string[];
    accountLocationAttributes?: AccountLocationAttributes;
  }[];
}

const PICKUP = 'pick_up';
const DROPOFF = 'drop_off';

const defaultWaypoint = {
  anchor: false,
  id: 0,
  passengerIdsStrings: [],
  accountLocationAttributes: {
    address: '',
    accountId: 0,
    placeId: '',
    streetAddressTwo: '',
  },
};

export const RideForm: FC<RideFormProps> = ({
  timeZone,
  ridersOnRide,
  organizers,
  rideDetailsDefault,
  handleSubmit,
  isNewRide = false,
}) => {
  const methods = useFormContext();
  const {
    control,
    register,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = methods;
  const {
    locationOptions,
    accountLocationOptions,
    isFetching: isFetchingLocations,
    debouncedHandleQuerySearch,
  } = useLocationOptions({
    accountId: rideDetailsDefault.accountId,
  });
  const [currentRidersOnRide, setCurrentRidersOnRide] = useState<RiderOnRide[]>(ridersOnRide);
  const {
    ridersOnRideOptions,
    accountRiderOptions,
    isFetching: isFetchingRiders,
    debouncedHandleQuerySearch: debouncedHandleRiderSearch,
  } = useRiderGroupedOptions({
    ridersOnRide: currentRidersOnRide,
    accountId: rideDetailsDefault.accountId,
  });
  const leadOrganizerName = getLeadOrganizerName(organizers);

  const { fields, move, append, insert, remove } = useFieldArray({
    control,
    name: 'waypointsAttributes',
  });

  const rideType = useWatch({
    control,
    name: 'timeAnchor',
    defaultValue: rideDetailsDefault.timeAnchor || DROPOFF,
  });

  const watchTime = useWatch({
    control,
    name: 'time',
    defaultValue: rideDetailsDefault.time,
  });
  const watchWaypoints = useWatch({
    control,
    name: 'waypointsAttributes',
    defaultValue: rideDetailsDefault.waypointsAttributes,
  });

  const watchAdjustmentReasonKey = useWatch({
    control,
    name: 'adjustmentReasonKey',
  });

  const setChangedFields = useCallback(
    (data) => {
      const dirtiedFields = isNewRide ? data : getDirtyFields(data, rideDetailsDefault, timeZone);

      if (isEmpty(dirtiedFields)) {
        return {};
      }

      const editRideAnchorTime =
        dirtiedFields.time && dirtiedFields.anchorTime
          ? mergeDateTime(dirtiedFields.anchorTime, timeZone, dirtiedFields.time)
          : null;

      const changedFields = {
        ...dirtiedFields,
        ...(editRideAnchorTime && {
          anchorTime: editRideAnchorTime,
        }),
      };

      return changedFields;
    },
    [rideDetailsDefault]
  );

  const handleAddRiderToRide = (isAnchor, waypointIndex, selectedRiders) => {
    if (!isAnchor) {
      const anchorIndex = helpers[rideType].getAnchorWaypointIndex(watchWaypoints.length);
      const anchorRiderIds = watchWaypoints[anchorIndex].passengerIdsStrings;
      const nextAnchorRiderIds = [
        ...anchorRiderIds,
        ...selectedRiders
          .filter((rider) => anchorRiderIds.indexOf(rider.value.toString()) === -1)
          .map((rider) => rider.value.toString()),
      ];

      setValue(`waypointsAttributes.${anchorIndex}.passengerIdsStrings`, nextAnchorRiderIds);
      setCurrentRidersOnRide([
        ...currentRidersOnRide,
        ...selectedRiders
          .filter((rider) => anchorRiderIds.indexOf(rider.value.toString()) === -1)
          .map((rider) => ({
            id: rider.value,
            slug: rider.slug,
            firstName: rider.label.split(' ')[0],
            lastName: rider.label.split(' ')[1],
            profileUrl: '',
            profilePictureUrl: rider.profilePictureUrl,
          })),
      ]);
    } else {
      const firstNonAnchorWaypointIndex = watchWaypoints.findIndex((waypoint) => !waypoint.anchor);

      setCurrentRidersOnRide([
        ...selectedRiders.map((rider) => ({
          id: rider.value,
          slug: rider.slug,
          firstName: rider.label.split(' ')[0],
          lastName: rider.label.split(' ')[1],
          profileUrl: '',
          profilePictureUrl: rider.profilePictureUrl,
        })),
      ]);
      setValue(
        `waypointsAttributes.${firstNonAnchorWaypointIndex}.passengerIdsStrings`,
        selectedRiders.map((rider) => rider.value.toString())
      );
    }
    setValue(
      `waypointsAttributes.${waypointIndex}.passengerIdsStrings`,
      selectedRiders.map((rider) => rider.value.toString())
    );
  };

  const handleRemoveRiderFromRide = (optionToDelete) => {
    const riderIdToDelete = optionToDelete.toString();

    watchWaypoints.forEach((waypoint, waypointIndex) => {
      // removes rider from all waypoints found
      if (includes(waypoint.passengerIdsStrings, riderIdToDelete)) {
        const selectedPassengerIds = watchWaypoints[waypointIndex]?.passengerIdsStrings;

        setValue(
          `waypointsAttributes.${waypointIndex}.passengerIdsStrings`,
          selectedPassengerIds?.filter((riderId) => riderId !== riderIdToDelete)
        );
      }

      setCurrentRidersOnRide(currentRidersOnRide.filter((rider) => rider.id.toString() !== riderIdToDelete));
    });
  };

  return (
    <form
      id="ride-form"
      onSubmit={methods.handleSubmit((data) => {
        const changedFields = setChangedFields(data);

        !isEmpty(changedFields) &&
          handleSubmit({
            ...changedFields,
            waypointsAttributes: changedFields.waypointsAttributes || [],
          });
      })}
    >
      <FormWrapper>
        <Box display="flex" flexDirection="column" gap={1} width="374px">
          <Text variant="h6">Route Anchor</Text>
          <Controller
            control={control}
            name="timeAnchor"
            defaultValue={DROPOFF}
            render={({ field: { onChange, value } }) => (
              <Toggle
                value={value}
                onChange={(_, newValue) => {
                  if (!newValue) {
                    return;
                  }
                  onChange(newValue);
                  reverseWaypoints();
                }}
                exclusive
              >
                <Toggle.Option value={PICKUP}>
                  <>
                    {'Pickup route'}
                    {typeof value === 'string' && value === PICKUP && (
                      <Svg icon="anchor-rounded" style={{ color: colorTokens.primary.main, marginLeft: '10px' }} />
                    )}
                  </>
                </Toggle.Option>
                <Toggle.Option value={DROPOFF}>
                  <>
                    {'Drop-off route'}
                    {typeof value === 'string' && value === DROPOFF && (
                      <Svg icon="anchor-rounded" style={{ color: colorTokens.primary.main, marginLeft: '10px' }} />
                    )}
                  </>
                </Toggle.Option>
              </Toggle>
            )}
          />
        </Box>
        <Text variant="h6">{`${helpers[rideType].getRouteTypeLabel()} date and time`}</Text>
        <Box display="flex" flexDirection="row" gap="84px">
          <InputLabel fieldtype="required" htmlFor="startDate">
            Date
          </InputLabel>
          <Controller
            control={control}
            name="anchorTime"
            defaultValue={rideDetailsDefault?.anchorTime}
            render={({ field: { onChange, value } }) => (
              <Box display="flex" gap={'14px'}>
                <DatePicker
                  onChange={onChange}
                  value={new Date(value || '')}
                  format="M/dd/yyyy"
                  slotProps={{
                    field: {
                      style: { maxWidth: '154px' },
                    },
                    textField: {
                      id: 'ride-date-picker',
                      error: !!errors.anchorTime,
                      helperText: errors.anchorTime?.message as string,
                      inputProps: {
                        'data-testid': 'ride-date-picker',
                      },
                    },
                  }}
                />
              </Box>
            )}
          />
        </Box>
        <Box display="flex" flexDirection="row" gap="84px">
          <InputLabel fieldtype="required" htmlFor="startTime">
            Time
          </InputLabel>
          <Controller
            control={control}
            name="time"
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <TimePicker
                time={value || watchTime}
                onChange={onChange}
                validationErrorMessage={(errors.time?.message as string) || ''}
              />
            )}
          />
        </Box>
        {(watchAdjustmentReasonKey || errors.adjustmentReasonKey) && (
          <AdjustedArrivalFormField control={control} errors={errors} />
        )}
        <Text variant="h6">Pickup and drop-off information</Text>
        <DragAndDrop
          items={fields}
          handleDragEnd={(event) => {
            const { active, over } = event;

            if (!over) {
              return;
            }
            move(active.data.current?.sortable.index, over.data.current?.sortable.index);
          }}
        >
          {fields.map((item, index) => {
            // we use the values from the watch because the `fields` value is just the defaultValues
            const values = getValues(`waypointsAttributes.${index}`);
            const isAnchor = helpers[rideType].isAnchor(index, fields.length);
            const disableItem = isAnchor;

            return (
              <div key={`waypointsAttributes-${item.id}`}>
                <DragAndDrop.SortableItem itemId={item.id} isDisabled={disableItem}>
                  <Timeline.Item
                    dotContent={
                      <DragAndDrop.DragHandle itemId={item.id} isDisabled={disableItem}>
                        <WaypointDragHandle isAnchorWaypoint={isAnchor}>{index + 1}</WaypointDragHandle>
                      </DragAndDrop.DragHandle>
                    }
                    connector={false}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <Box display="flex" flexDirection="row" gap="8px" alignItems="center">
                        <Text variant="h6" style={{ color: colorTokens.primary.main }}>
                          {helpers[rideType].getWaypointTitle(index, fields.length)}
                        </Text>
                        {isAnchor && <Svg icon="anchor-rounded" style={{ color: colorTokens.primary.main }} />}
                      </Box>
                      <Box display="flex" flexDirection="row" gap="8px" alignItems="center">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                          <RiderDropdown
                            waypointIndex={index}
                            isAnchorWaypoint={isAnchor}
                            riderOptions={[...ridersOnRideOptions, ...accountRiderOptions]}
                            isFetching={isFetchingRiders}
                            ridersOnRide={currentRidersOnRide}
                            organizerName={leadOrganizerName}
                            onAdd={(selectedRiders) => handleAddRiderToRide(isAnchor, index, selectedRiders)}
                            onRemove={handleRemoveRiderFromRide}
                            renderInput={(params: AutocompleteRenderInputParams) => (
                              <FormInputText
                                {...params}
                                variant="outlined"
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <>
                                      <Box mr="32px">
                                        <Svg icon="sidebar_riders" />
                                      </Box>
                                      <Box
                                        ml={
                                          (isAnchor
                                            ? ridersOnRide.map((rider) => rider.id.toString())
                                            : values?.passengerIdsStrings
                                          ).length
                                            ? '32px'
                                            : '8px'
                                        }
                                      >
                                        {params.InputProps.startAdornment}
                                      </Box>
                                    </>
                                  ),
                                }}
                                onChange={(e) => {
                                  debouncedHandleRiderSearch(e.target.value);
                                }}
                                error={
                                  !!(
                                    errors.waypointsAttributes && errors.waypointsAttributes[index]?.passengerIdsStrings
                                  )
                                }
                                helperText={
                                  errors.waypointsAttributes &&
                                  errors.waypointsAttributes[index]?.passengerIdsStrings?.message
                                }
                              />
                            )}
                          />
                          <LocationSearchDropdown
                            id={`waypointsAttributes_${index}_location_address`}
                            options={[...accountLocationOptions, ...locationOptions]}
                            isFetching={isFetchingLocations}
                            inputValue={values?.accountLocationAttributes?.address}
                            setInputValue={(selectedLocation) => {
                              debouncedHandleRiderSearch('');

                              if (!selectedLocation?.label) {
                                return '';
                              }

                              setValue(
                                `waypointsAttributes.${index}.accountLocationAttributes.address`,
                                selectedLocation.label
                              );

                              setValue(
                                `waypointsAttributes.${index}.accountLocationAttributes.streetAddressTwo`,
                                selectedLocation?.streetAddressTwo
                              );
                              setValue(
                                `waypointsAttributes.${index}.accountLocationAttributes.placeId`,
                                selectedLocation?.value
                              );

                              return selectedLocation.label;
                            }}
                            onChange={(_, selectedLocation) => {
                              if (!selectedLocation) {
                                setValue(`waypointsAttributes.${index}.accountLocationAttributes.address`, undefined);
                                setValue(
                                  `waypointsAttributes.${index}.accountLocationAttributes.streetAddressTwo`,
                                  undefined
                                );
                                setValue(`waypointsAttributes.${index}.accountLocationAttributes.placeId`, undefined);
                              }

                              return selectedLocation;
                            }}
                            renderInput={(params: AutocompleteRenderInputParams) => (
                              <FormInputText
                                {...params}
                                variant="outlined"
                                placeholder="Enter location"
                                fullWidth
                                onChange={(e) => {
                                  debouncedHandleQuerySearch(e.target.value);
                                }}
                                error={
                                  !!(
                                    errors.waypointsAttributes &&
                                    errors.waypointsAttributes[index]?.accountLocationAttributes?.placeId
                                  )
                                }
                                helperText={
                                  errors.waypointsAttributes &&
                                  errors.waypointsAttributes[index]?.accountLocationAttributes?.placeId?.message
                                }
                              />
                            )}
                          />
                          <Box display="flex" flexDirection="row" alignItems="center" gap="24px">
                            <InputLabel
                              id="streetAddressTwo"
                              style={{ color: colorTokens.text.secondary, whiteSpace: 'nowrap' }}
                            >
                              Apt #, Suite, Floor, etc
                            </InputLabel>
                            <Controller
                              control={control}
                              name={`waypointsAttributes.${index}.accountLocationAttributes.streetAddressTwo`}
                              defaultValue={values?.accountLocationAttributes.streetAddressTwo}
                              render={({ field: { onChange, value } }) => (
                                <FormInputText
                                  variant="outlined"
                                  placeholder="e.g. Floor 3"
                                  fullWidth
                                  onChange={onChange}
                                  value={value}
                                />
                              )}
                            />
                          </Box>
                        </div>
                        <div style={{ minWidth: '24px' }}>
                          {isAnchor && (
                            <IconButton
                              onClick={() =>
                                rideType === DROPOFF
                                  ? insert(watchWaypoints.length - 1, defaultWaypoint)
                                  : append(defaultWaypoint)
                              }
                              size="small"
                              style={{ background: colorTokens.primary.focus }}
                            >
                              <AddRoundedIcon
                                width="24px"
                                height="24px"
                                style={{ color: colorTokens.text.secondary }}
                              />
                            </IconButton>
                          )}
                          {!isAnchor && watchWaypoints.length > 2 && (
                            <IconButton
                              onClick={() => {
                                const ridersToDelete = values?.passengerIdsStrings || [];

                                ridersToDelete.forEach((riderId) => {
                                  handleRemoveRiderFromRide(riderId);
                                });

                                remove(index);
                              }}
                              size="small"
                              style={{ background: colorTokens.primary.focus }}
                            >
                              <RemoveRoundedIcon
                                width="24px"
                                height="24px"
                                style={{ color: colorTokens.text.secondary }}
                              />
                            </IconButton>
                          )}
                        </div>
                      </Box>

                      <input
                        type="hidden"
                        {...register(`waypointsAttributes.${index}.passengerIdsStrings`, {
                          value: isAnchor
                            ? ridersOnRide.map((rider) => rider.id.toString())
                            : values?.passengerIdsStrings,
                        })}
                      />
                      <input
                        type="hidden"
                        {...register(`waypointsAttributes.${index}.anchor`, {
                          value: helpers[rideType].isAnchor(index, watchWaypoints.length),
                        })}
                      />
                      <input
                        type="hidden"
                        {...register(`waypointsAttributes.${index}.accountLocationAttributes.address`, {
                          value: values?.accountLocationAttributes.address,
                        })}
                      />
                      <input
                        type="hidden"
                        {...register(`waypointsAttributes.${index}.accountLocationAttributes.placeId`, {
                          value: values?.accountLocationAttributes.placeId,
                        })}
                      />
                    </div>
                  </Timeline.Item>
                </DragAndDrop.SortableItem>
              </div>
            );
          })}
        </DragAndDrop>
      </FormWrapper>
    </form>
  );

  function reverseWaypoints() {
    const currentFormValues = getValues();

    reset({
      ...rideDetailsDefault,
      ...currentFormValues,
      waypointsAttributes: currentFormValues.waypointsAttributes.reverse(),
    });
  }
};

const helpers = {
  [PICKUP]: {
    getWaypointTitle: (index) => (index ? 'Drop-off' : 'Pickup'),
    getWaypointType: (index) => (index ? DROPOFF : PICKUP),
    getAnchorWaypointIndex: () => 0,
    isAnchor: (index) => !index,
    getRouteTypeLabel: () => 'Pickup',
  },
  [DROPOFF]: {
    getWaypointTitle: (index, waypointCount) => (index + 1 === waypointCount ? 'Drop-off' : 'Pickup'),
    getWaypointType: (index, waypointCount) => (index + 1 === waypointCount ? DROPOFF : PICKUP),
    isAnchor: (index, waypointCount) => index + 1 === waypointCount,
    getAnchorWaypointIndex: (waypointCount) => waypointCount - 1,
    getRouteTypeLabel: () => 'Drop-off',
  },
};

const WaypointDragHandle = ({ children, isAnchorWaypoint }) => {
  const [hover, setHover] = useState(false);

  return (
    <OutlinedCircle $isCompleted onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      {hover && !isAnchorWaypoint ? <Svg icon="move" /> : children}
    </OutlinedCircle>
  );
};
